import { DATE_FORMAT_OPTIONS, SETTINGS_LIMITS, type OperationalSettings, type SettingsPatch } from '@tes/domain';

export const NUMBER_SETTINGS = [
  { group: 'signing', key: 'linkExpiryDays', label: 'Signing link expiry (days)', hint: 'Default for new envelopes. Existing signing links keep their expiry dates; an API request can override the default.' },
  { group: 'signing', key: 'retentionDays', label: 'Keep working files (days)', hint: 'Time after completion, cancellation, decline or expiry before PDFs, audit certificates and signature images are deleted. Applies to existing files too.' },
  { group: 'uploads', key: 'uploadTtlHours', label: 'Temporary upload lifetime (hours)', hint: 'Time to use an uploaded PDF in an envelope. Cleanup also removes source uploads this long after use; the envelope keeps its own copy.' },
  { group: 'webhooks', key: 'maxAttempts', label: 'Maximum delivery attempts', hint: 'Includes the first attempt. Applies when a delivery is newly queued or replayed; queued retries keep their current policy.' },
  { group: 'webhooks', key: 'backoffMs', label: 'Initial retry delay (milliseconds)', hint: 'The first retry waits this long; each later retry doubles the delay. Applies to newly queued or replayed deliveries.' },
  { group: 'webhooks', key: 'timeoutMs', label: 'Response timeout (milliseconds)', hint: 'How long each delivery attempt waits for the receiving endpoint. Applies to the next attempt.' },
] as const;

export type SettingsDraft = Partial<Record<typeof NUMBER_SETTINGS[number]['key'] | 'timezone' | 'dateFormat', string>>;

export function numberSettingValue(settings: OperationalSettings, field: typeof NUMBER_SETTINGS[number]): number {
  // Keys are declared together with their groups above.
  return (settings[field.group] as unknown as Record<string, number>)[field.key];
}

/** Validate only edited values, preserving untouched deployment defaults and concurrent edits. */
export function settingsPatch(draft: SettingsDraft): { patch: SettingsPatch; errors: string[] } {
  const patch: SettingsPatch = {};
  const errors: string[] = [];
  for (const field of NUMBER_SETTINGS) {
    if (draft[field.key] === undefined) continue;
    const raw = draft[field.key]!.trim();
    const value = Number(raw);
    const { min, max } = SETTINGS_LIMITS[field.key];
    if (!raw || !Number.isInteger(value) || value < min || value > max) {
      errors.push(`${field.label} must be a whole number from ${min} to ${max}.`);
    } else {
      Object.assign(patch, { [field.group]: { ...patch[field.group], [field.key]: value } });
    }
  }
  if (draft.timezone !== undefined) {
    const timezone = draft.timezone.trim();
    try {
      if (!timezone) throw new Error('Empty timezone');
      new Intl.DateTimeFormat('en', { timeZone: timezone }).format();
      patch.platform = { timezone };
    } catch {
      errors.push('Choose a valid timezone, such as Australia/Perth or UTC.');
    }
  }
  if (draft.dateFormat !== undefined) {
    if (!DATE_FORMAT_OPTIONS.some((option) => option.value === draft.dateFormat)) {
      errors.push('Choose one of the available date formats.');
    } else {
      patch.platform = { ...patch.platform, dateFormat: draft.dateFormat };
    }
  }
  return { patch, errors };
}
